import React, { useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useNarrowLayout,
  useMobilePortrait,
  AIRECORDING_FUSE_METRICS_SESSION_KEY,
} from '../hooks/use-narrow-layout';
import {
  persistAiRecordingSidebarState,
  readAiRecordingSidebarCollapsed,
} from '../lib/sidebar-state';

/** Ícono panel-left (mismo que Admin Center) */
const PanelLeftIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    color="inherit"
    {...props}
  >
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M9 4v16" />
  </svg>
);

const STORAGE_KEY = 'aiRecording:leftSidebarState';
const SIDEBAR_WIDTH = 314;
const SIDEBAR_WIDTH_COLLAPSED = 64;
const POST_MESSAGE_SOURCE = 'aiRecording';

/** Left sidebar above main column hit-testing (fixes dead clicks when fixed sidebar shares viewport with flex content). */
const SIDEBAR_Z_BASE = 1200;
const SIDEBAR_Z_DRAWER_OPEN = 1400;

export interface AdminCloneSidebarItem {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  badge?: React.ReactNode;
}

interface AdminCenterLeftSidebarCloneProps {
  moduleName: string;
  moduleInitials: string;
  items: AdminCloneSidebarItem[];
  activeTab: string;
  onNavigate: (id: string) => void;
  schoolLogoUrl?: string | null;
  schoolName?: string | null;
  logoLoading?: boolean;
  isFromACV?: boolean;
  quickAction?: React.ReactNode;
  onCollapseChange?: (collapsed: boolean) => void;
}

export function AdminCenterLeftSidebarClone({
  moduleName,
  moduleInitials,
  items,
  activeTab,
  onNavigate,
  schoolLogoUrl,
  schoolName,
  logoLoading = false,
  isFromACV = true,
  quickAction,
  onCollapseChange,
}: AdminCenterLeftSidebarCloneProps) {
  const { t } = useTranslation();
  const narrow = useNarrowLayout();
  const isMobilePortrait = useMobilePortrait();

  const [isCollapsed, setIsCollapsed] = useState(() =>
    readAiRecordingSidebarCollapsed(STORAGE_KEY),
  );
  // Width animation only after an explicit user/navbar toggle — never on module switch.
  const [animateWidth, setAnimateWidth] = useState(false);

  /** Narrow-only drawer open (314px + overlay); desktop uses isCollapsed only. */
  const [drawerOpen, setDrawerOpen] = useState(false);

  useLayoutEffect(() => {
    if (!narrow) setDrawerOpen(false);
  }, [narrow]);

  const persistCollapsed = useCallback((value: boolean) => {
    persistAiRecordingSidebarState(value, STORAGE_KEY);
  }, []);

  const effectiveCollapsed = narrow ? !drawerOpen : isCollapsed;
  const effectiveWidth = effectiveCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  // P2 — portrait+narrow+closed: hide sidebar completely (no 64px rail).
  // Same pattern as FEB/DashboardSidebar and AITutor/StandardLeftSidebar (bitacora-040526).
  const isHiddenInPortrait = isMobilePortrait && narrow && !drawerOpen;

  const toggleSidebar = useCallback(() => {
    setAnimateWidth(true);
    try {
      document.documentElement.setAttribute('data-air-sidebar-animate', '1');
    } catch {
      /* ignore */
    }
    if (narrow) {
      setDrawerOpen((o) => !o);
    } else {
      setIsCollapsed((c) => {
        const next = !c;
        persistCollapsed(next);
        return next;
      });
    }
  }, [narrow, persistCollapsed]);

  const expandSidebar = useCallback(() => {
    if (narrow) {
      setDrawerOpen(true);
    } else {
      setIsCollapsed(false);
      persistCollapsed(false);
    }
  }, [narrow, persistCollapsed]);

  const collapseSidebar = useCallback(() => {
    if (narrow) {
      setDrawerOpen(false);
    } else {
      setIsCollapsed(true);
      persistCollapsed(true);
    }
  }, [narrow, persistCollapsed]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--acv-sidebar-width', `${effectiveWidth}px`);
  }, [effectiveWidth]);

  useEffect(() => {
    // P2 — when portrait-hidden, report width:0 so ACVLayout applies no offset.
    const reportedWidth = isHiddenInPortrait ? 0 : effectiveWidth;
    const reportedCollapsed = isHiddenInPortrait ? false : !!effectiveCollapsed;
    try {
      sessionStorage.setItem(
        AIRECORDING_FUSE_METRICS_SESSION_KEY,
        JSON.stringify({
          width: reportedWidth,
          collapsed: reportedCollapsed,
        }),
      );
    } catch {
      // ignore
    }
    onCollapseChange?.(effectiveCollapsed);
    window.dispatchEvent(
      new CustomEvent('sidebar-state-change', {
        detail: { fuseWidth: reportedWidth },
      }),
    );
    try {
      if (typeof window === 'undefined' || !window.parent || window.parent === window) return;
      window.parent.postMessage(
        {
          type: 'acv-leftsidebar-metrics',
          source: POST_MESSAGE_SOURCE,
          width: reportedWidth,
          collapsed: reportedCollapsed,
          ts: Date.now(),
        },
        '*',
      );
    } catch {
      // ignore
    }
  }, [effectiveCollapsed, effectiveWidth, isHiddenInPortrait, onCollapseChange]);

  useEffect(() => {
    const cls = 'acv-aiRecording-leftsidebar-collapsed';
    // P2 — when sidebar is portrait-hidden it is NOT "collapsed" (rail is hidden).
    // Adding the class in that state would hide the ACV navbar panel-left toggle
    // (ACV/App.css pattern), leaving the user with no way to open the sidebar.
    const shouldMark = effectiveCollapsed && !isHiddenInPortrait;
    if (shouldMark) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => {
      document.body.classList.remove(cls);
    };
  }, [effectiveCollapsed, isHiddenInPortrait]);

  useEffect(() => {
    const onToggleFromHost = () => {
      toggleSidebar();
    };
    const onExpandFromHost = () => {
      expandSidebar();
    };
    const onForceCollapsed = () => {
      collapseSidebar();
    };
    const onForceExpanded = () => {
      expandSidebar();
    };
    const onSetState = (e: Event) => {
      const collapsed = (e as CustomEvent<{ collapsed: boolean }>).detail?.collapsed;
      if (typeof collapsed !== 'boolean') return;
      setIsCollapsed(collapsed);
      persistCollapsed(collapsed);
    };
    window.addEventListener('acv-toggle-left-sidebar-from-host', onToggleFromHost);
    window.addEventListener('acv-expand-left-sidebar-from-host', onExpandFromHost);
    window.addEventListener('acv-force-left-sidebar-collapsed', onForceCollapsed);
    window.addEventListener('acv-force-left-sidebar-expanded', onForceExpanded);
    window.addEventListener('acv-set-left-sidebar-state', onSetState);
    return () => {
      window.removeEventListener('acv-toggle-left-sidebar-from-host', onToggleFromHost);
      window.removeEventListener('acv-expand-left-sidebar-from-host', onExpandFromHost);
      window.removeEventListener('acv-force-left-sidebar-collapsed', onForceCollapsed);
      window.removeEventListener('acv-force-left-sidebar-expanded', onForceExpanded);
      window.removeEventListener('acv-set-left-sidebar-state', onSetState);
    };
  }, [toggleSidebar, expandSidebar, collapseSidebar, persistCollapsed]);

  const handleNavigate = (id: string) => {
    onNavigate(id);
    if (narrow) setDrawerOpen(false);
  };

  const sidebarZ = narrow && drawerOpen ? SIDEBAR_Z_DRAWER_OPEN : SIDEBAR_Z_BASE;

  return (
    <>
      {narrow && drawerOpen && (
        <div
          role="presentation"
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            // P6 — portrait: overlay anchors right after the drawer (right-strip ~20%).
            // Landscape/desktop: full-screen backdrop as usual.
            left: isMobilePortrait
              ? `calc(var(--acv-vertical-navbar-width, 0px) + ${effectiveWidth}px)`
              : 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--sidebar-overlay-bg, rgba(0, 0, 0, 0.45))',
            zIndex: SIDEBAR_Z_BASE - 1,
          }}
        />
      )}
      <aside
        className="sidebar admin-center-left-sidebar-clone"
        style={{
          position: 'fixed',
          top: 0,
          left: 'var(--acv-vertical-navbar-width, 0px)',
          height: '100vh',
          width: effectiveWidth,
          minWidth: effectiveWidth,
          backgroundColor: 'var(--sidebar-bg, var(--card-bg, #ffffff))',
          color: 'var(--sidebar-foreground, var(--text-primary, #1a1a1a))',
          borderRight: 'var(--sidebar-border, 1px solid #e0e0e0)',
          boxShadow: 'var(--sidebar-shadow, 2px 0 8px rgba(0,0,0,0.08))',
          // P2 — portrait+narrow+closed: remove from layout entirely (no 64px rail).
          display: isHiddenInPortrait ? 'none' : 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          transition: animateWidth
            ? 'width var(--sidebar-transition-duration, 300ms) var(--sidebar-transition-timing, cubic-bezier(0.4, 0, 0.2, 1))'
            : 'none',
          overflow: 'hidden',
          zIndex: sidebarZ,
        }}
      >
        <div
          className="sidebar-header"
          style={{
            padding: effectiveCollapsed ? '12px 0' : '16px 16px 10px 16px',
            backgroundColor: 'var(--sidebar-header-bg, var(--sidebar-bg, #ffffff))',
            color: 'var(--sidebar-header-foreground, var(--sidebar-foreground, #1a1a1a))',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: effectiveCollapsed ? 0 : 10,
            flexShrink: 0,
            minHeight: effectiveCollapsed ? 52 : undefined,
          }}
        >
          {effectiveCollapsed ? (
            <button
              type="button"
              aria-label={t('sidebar.expand', 'Expand menu')}
              onClick={expandSidebar}
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                backgroundColor: 'transparent',
                color: 'var(--sidebar-foreground, var(--text-primary, #1a1a1a))',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PanelLeftIcon style={{ width: 22, height: 22 }} />
            </button>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minWidth: 0 }}>
                {schoolLogoUrl ? (
                  <img
                    src={schoolLogoUrl}
                    alt={schoolName || moduleName}
                    style={{
                      width: '100%',
                      maxWidth: 220,
                      height: 44,
                      objectFit: 'contain',
                      borderRadius: 6,
                    }}
                  />
                ) : logoLoading ? (
                  <div
                    style={{
                      width: '100%',
                      maxWidth: 220,
                      height: 44,
                      borderRadius: 6,
                      backgroundColor: 'var(--sidebar-item-hover-bg, rgba(0,0,0,0.06))',
                      flexShrink: 0,
                    }}
                    aria-hidden
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      maxWidth: 220,
                      height: 44,
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 13,
                      letterSpacing: '0.3px',
                      color: 'var(--sidebar-foreground, #1a1a1a)',
                      background: 'var(--sidebar-item-hover-bg, rgba(0,0,0,0.06))',
                      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.10)',
                    }}
                  >
                    {(moduleInitials || moduleName || 'AR').slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 400,
                  lineHeight: 1.15,
                  color: 'var(--sidebar-header-foreground, var(--text-primary, #1a1a1a))',
                  width: '100%',
                  textAlign: 'left',
                  alignSelf: 'stretch',
                }}
              >
                {moduleName}
              </div>
              <button
                type="button"
                aria-label={t('sidebar.collapse', 'Collapse menu')}
                onClick={collapseSidebar}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  backgroundColor: 'transparent',
                  color: 'var(--sidebar-foreground, var(--text-primary, #1a1a1a))',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  visibility: isFromACV ? 'hidden' : 'visible',
                  pointerEvents: isFromACV ? 'none' : 'auto',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* P12 — close drawer when user taps quick action in narrow/portrait mode. */}
        {quickAction && (!effectiveCollapsed || narrow) ? (
          <div
            style={{ padding: '0 12px 8px' }}
            onClick={() => { if (narrow) setDrawerOpen(false); }}
          >
            {quickAction}
          </div>
        ) : null}

        <nav
          className={`sidebar-content${effectiveCollapsed ? ' sidebar-content--collapsed' : ''}`}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: effectiveCollapsed ? 8 : 'var(--sidebar-padding, 16px)',
            display: 'flex',
            flexDirection: 'column',
            gap: effectiveCollapsed ? 2 : 4,
          }}
        >
          {items.map((item) => {
            const isSelected = activeTab === item.id;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavigate(item.id)}
                style={{
                  marginBottom: effectiveCollapsed ? 0 : 4,
                  padding: effectiveCollapsed ? 8 : 'var(--sidebar-item-padding, 12px 16px)',
                  backgroundColor: isSelected
                    ? 'var(--sidebar-item-selected-bg, var(--button-primary-bg, var(--theme-primary, #1976d2)))'
                    : 'var(--sidebar-item-bg, transparent)',
                  color: isSelected
                    ? 'var(--sidebar-item-selected-foreground, var(--button-primary-foreground, #ffffff))'
                    : 'var(--sidebar-item-foreground, var(--text-primary, #1a1a1a))',
                  border: 'none',
                  outline: 'none',
                  borderRadius: 'var(--sidebar-item-border-radius, 8px)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: effectiveCollapsed ? 0 : 12,
                  justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                  width: '100%',
                  minWidth: 0,
                  textAlign: 'left',
                  fontSize: 14,
                  fontWeight: 400,
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== item.id) {
                    e.currentTarget.style.backgroundColor = 'var(--sidebar-item-hover-bg, #f5f5f5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== item.id) {
                    e.currentTarget.style.backgroundColor = 'var(--sidebar-item-bg, transparent)';
                  }
                }}
              >
                {Icon && (
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      backgroundColor: 'transparent',
                      color: 'inherit',
                    }}
                  >
                    <Icon className="acv-nav-item-icon" style={{ width: 18, height: 18 }} />
                  </span>
                )}
                {!effectiveCollapsed && (
                  <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.label}
                  </span>
                )}
                {!effectiveCollapsed && (item.badge ?? null)}
              </button>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
